const SESSION_COOKIE = "mp_sid";
const ANON_COOKIE = "mp_aid";
const ATTR_COOKIE = "mp_attr";
const FIRST_PATH_COOKIE = "mp_first_path";

export type Attribution = {
  first?: Record<string, string>;
  last?: Record<string, string>;
  referrer?: string;
};

const ATTR_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "msclkid",
  "fbclid",
  "ttclid",
];

function parseQueryParams(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  const sp = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  for (const k of ATTR_PARAM_KEYS) {
    const v = sp.get(k);
    if (v && v.trim()) out[k] = v.trim();
  }
  return out;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}=([^;]*)`));
  return m ? decodeURIComponent(m[1]!) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax`;
}

function safeJsonParse<T>(v: string | null): T | null {
  if (!v) return null;
  try {
    return JSON.parse(v) as T;
  } catch {
    return null;
  }
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function getOrCreateSessionId(): string {
  const existing = readCookie(SESSION_COOKIE);
  if (existing) {
    // Keep this a rolling 30-minute session rather than expiring 30 minutes
    // after the first event even while the person remains active.
    writeCookie(SESSION_COOKIE, existing, 30 * 60);
    return existing;
  }
  const sid = randomId();
  // 30 minutes.
  writeCookie(SESSION_COOKIE, sid, 30 * 60);
  return sid;
}

export function getOrCreateAnonymousId(): string {
  const existing = readCookie(ANON_COOKIE);
  if (existing) return existing;
  const aid = randomId();
  // 1 year.
  writeCookie(ANON_COOKIE, aid, 365 * 24 * 60 * 60);
  return aid;
}

export function updateAttributionFromLocation(): Attribution {
  if (typeof window === "undefined") return {};

  const existing = safeJsonParse<Attribution>(readCookie(ATTR_COOKIE)) ?? {};
  const utm = parseQueryParams(window.location.search);
  const next: Attribution = {
    ...existing,
    referrer: existing.referrer ?? (document.referrer || undefined),
  };

  const hasUtm = Object.keys(utm).length > 0;
  if (hasUtm) {
    if (!next.first) next.first = utm;
    next.last = utm;
  }

  writeCookie(ATTR_COOKIE, JSON.stringify(next), 365 * 24 * 60 * 60);
  return next;
}

export function getAttribution(): Attribution {
  return safeJsonParse<Attribution>(readCookie(ATTR_COOKIE)) ?? {};
}

export function getOrCreateFirstLandingPath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const existing = readCookie(FIRST_PATH_COOKIE);
  if (existing) return existing;
  const firstPath = `${window.location.pathname}${window.location.search}`;
  writeCookie(FIRST_PATH_COOKIE, firstPath, 365 * 24 * 60 * 60);
  return firstPath;
}
